import React, { useState, useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { Toaster } from 'react-hot-toast';
import { AuthProvider, useAuth } from './context/AuthContext';
import { CreditProvider } from './context/CreditContext';

import AuthPage from './components/Auth/AuthPage';
import TopBar from './components/Layout/TopBar';
import Sidebar from './components/Layout/Sidebar';

import GeneratePage   from './pages/GeneratePage';
import EditPage       from './pages/EditPage';
import VideoPage      from './pages/VideoPage';
import LipSyncPage    from './pages/LipSyncPage';
import MotionSyncPage from './pages/MotionSyncPage';
import UpscalePage    from './pages/UpscalePage';
import EditVideoPage  from './pages/EditVideoPage';
import AnglesPage     from './pages/AnglesPage';
import MultiShotPage  from './pages/MultiShotPage';
import ProfilePage    from './pages/ProfilePage';
import SubscriptionPage  from './pages/SubscriptionPage';
import GalleryPage    from './pages/GalleryPage';
import WhatsNewPage   from './pages/WhatsNewPage';
import HelpCenterPage from './pages/HelpCenterPage';
import PrivacyPolicyPage from './pages/PrivacyPolicyPage';
import FeedbackPage   from './pages/FeedbackPage';
import GuidePage      from './pages/GuidePage';
import PromptPage     from './pages/PromptPage';
import BkashTermsPage from './pages/BkashTermsPage';
import EditProfilePage from './pages/EditProfilePage';
import AifieldHome    from './pages/HomePage';
import ExplorePage   from './pages/ExplorePage';
import Promptbook    from './pages/Promptbook';


import './App.css';

// ─────────────────────────────────────────────────────────────────────────────
// KEEP-ALIVE ROUTES
// All tool pages live here. Once a user visits a page for the first time,
// the component mounts and is NEVER unmounted again — it is simply hidden
// (display:none) when not active.
//
// WHY THIS MATTERS:
//  • All user input (prompts, uploaded files, reference images, trim state)
//    is preserved perfectly when switching tools.
//  • <img> and <video> elements stay in the DOM so the browser does NOT
//    re-request the assets from the server — eliminating bandwidth waste and
//    server load on every tab switch.
//  • No useEffect re-runs, no spurious API calls on navigation.
// ─────────────────────────────────────────────────────────────────────────────
const KEEP_ALIVE_ROUTES = [
  { path: '/generate',    key: 'generate',    Component: GeneratePage },
  { path: '/multishot',   key: 'multishot',   Component: MultiShotPage },
  { path: '/angles',      key: 'angles',      Component: AnglesPage },
  { path: '/edit',        key: 'edit',        Component: EditPage },
  { path: '/video',       key: 'video',       Component: VideoPage },
  { path: '/frame2video', key: 'frame2video', Component: VideoPage },
  { path: '/lipsync',     key: 'lipsync',     Component: LipSyncPage },
  { path: '/motionsync',  key: 'motionsync',  Component: MotionSyncPage },
  { path: '/upscale',     key: 'upscale',     Component: UpscalePage },
  { path: '/edit-video',  key: 'edit-video',  Component: EditVideoPage },
  { path: '/prompt',      key: 'prompt',      Component: PromptPage },
];

const KEEP_ALIVE_PATHS = new Set(KEEP_ALIVE_ROUTES.map(r => r.path));

// Wrapper style for an ACTIVE keep-alive page
const activeStyle = {
  display: 'flex',
  flex: 1,
  width: '100%',
  height: '100%',
  overflow: 'hidden',
  minWidth: 0,
};

// Wrapper style for a HIDDEN keep-alive page
// NOTE: we use display:none (not visibility:hidden) so hidden pages take
// zero layout space and cannot receive accidental pointer events.
const hiddenStyle = { display: 'none' };

function AppRoutes() {
  const { user, loading } = useAuth();
  const location = useLocation();

  const [showAuth, setShowAuth]                   = useState(false);
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);
  const [isMobileSidebarOpen, setIsMobileSidebarOpen] = useState(false);

  // Lazy mount: track which keep-alive pages have been visited.
  // Start with the initial path so the very first page is ready immediately.
  const [mountedPaths, setMountedPaths] = useState(() => {
    const initial = KEEP_ALIVE_PATHS.has(location.pathname)
      ? location.pathname
      : '/generate'; // default landing page
    return new Set([initial]);
  });

  const isKeepAlivePath = KEEP_ALIVE_PATHS.has(location.pathname);

  // When the user navigates to a keep-alive path for the first time, mount it.
  // Subsequent visits to the same path re-use the mounted instance (no-op here).
  useEffect(() => {
    if (isKeepAlivePath && !mountedPaths.has(location.pathname)) {
      setMountedPaths(prev => new Set([...prev, location.pathname]));
    }
  }, [location.pathname, isKeepAlivePath, mountedPaths]);

  // ── Loading splash ─────────────────────────────────────────────────────────
  if (loading) {
    return (
      <div style={{
        height: '100vh', display: 'flex', flexDirection: 'column',
        alignItems: 'center', justifyContent: 'center', gap: 16,
        background: 'var(--bg)',
      }}>
        <img src="/logo.png" alt="aiField logo" style={{ height: 48, width: 'auto', marginBottom: 8 }} />
        <div className="spinner" />
      </div>
    );
  }

  return (
    <CreditProvider>
      <div className="noise" />
      <Toaster position="top-right" toastOptions={{
        style: { background: '#151720', color: '#f0f0f5', border: '1px solid rgba(255,255,255,.08)' }
      }} />

      {/* Safety: timeout banner + suspension overlay (temporarily removed) */}

      {/* Standalone full-screen routes (no shell) */}
      <Routes>
        <Route path="/bkash-terms" element={<BkashTermsPage />} />
        <Route path="/home" element={<AifieldHome />} />
        <Route path="/" element={<Navigate to="/home" replace />} />

        {/* Main app shell — catch all */}
        <Route path="*" element={
          <>
            {/* ── Auth Modal ────────────────────────────────────────────── */}
            {showAuth && !user && (
              <div className="auth-modal-overlay" onClick={() => setShowAuth(false)}>
                <div className="auth-modal" onClick={e => e.stopPropagation()}>
                  <button className="auth-modal-close" onClick={() => setShowAuth(false)}>✕</button>
                  <AuthPage onSuccess={() => setShowAuth(false)} />
                </div>
              </div>
            )}

            <div className={`shell ${isSidebarCollapsed ? 'collapsed' : ''}`}>
              <TopBar
                onShowAuth={() => setShowAuth(true)}
                isSidebarCollapsed={isSidebarCollapsed}
                setIsSidebarCollapsed={setIsSidebarCollapsed}
                isMobileSidebarOpen={isMobileSidebarOpen}
                setIsMobileSidebarOpen={setIsMobileSidebarOpen}
              />
              <Sidebar
                onShowAuth={() => setShowAuth(true)}
                isCollapsed={isSidebarCollapsed}
                isMobileOpen={isMobileSidebarOpen}
                setIsMobileOpen={setIsMobileSidebarOpen}
              />

              <div className="content page-enter">

                {/* ── Keep-Alive Tool Pages ─────────────────────────────────
                    Never unmounted after first visit.
                    Hidden via CSS — state, files & media are all preserved.
                ──────────────────────────────────────────────────────────── */}
                {KEEP_ALIVE_ROUTES.map(({ path, key, Component }) => {
                  if (!mountedPaths.has(path)) return null;
                  return (
                    <div
                      key={key}
                      style={location.pathname === path ? activeStyle : hiddenStyle}
                    >
                      <Component onShowAuth={() => setShowAuth(true)} />
                    </div>
                  );
                })}

                {/* ── Standard Pages (mount/unmount normally) ───────────── */}
                {!isKeepAlivePath && (
                  <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden', minWidth: 0, width: '100%', height: '100%' }}>
                    <Routes>
                      <Route path="/profile"
                        element={user ? <ProfilePage /> : <Navigate to="/generate" replace />} />
                      <Route path="/edit-profile"
                        element={user ? <EditProfilePage /> : <Navigate to="/generate" replace />} />
                      <Route path="/subscription"
                        element={<SubscriptionPage onShowAuth={() => setShowAuth(true)} />} />
                      <Route path="/gallery"
                        element={user ? <GalleryPage /> : <Navigate to="/generate" replace />} />
                      <Route path="/explore" element={<ExplorePage onShowAuth={() => setShowAuth(true)} />} />
                      <Route path="/whats-new"  element={<WhatsNewPage />} />
                      <Route path="/guide"      element={<GuidePage />} />
                      <Route path="/help"       element={<HelpCenterPage />} />
                      <Route path="/feedback"   element={<FeedbackPage />} />
                      <Route path="/privacy"    element={<PrivacyPolicyPage />} />
                      <Route path="/promptbook" element={<Promptbook />} />
                      <Route path="*"           element={<Navigate to="/generate" replace />} />
                      <Route path="/"           element={<Navigate to="/generate" replace />} />
                    </Routes>
                  </div>
                )}

              </div>
            </div>
          </>
        } />
      </Routes>
    </CreditProvider>
  );
}

export default function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <AppRoutes />
      </AuthProvider>
    </BrowserRouter>
  );
}
